/**
 * BatteryHealthModal Component
 * Extracted from DeviceSpecifications.tsx
 * Handles iPhone battery health percentage input modal
 */

import React, { useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../utils/portalRoot';
import styled from 'styled-components';

// Styled Components
const ModalOverlay = styled.div<{ disableAnimation?: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .dark & {
    background: rgba(0, 0, 0, 0.65);
  }

  @media (min-width: 1024px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    align-items: flex-start;
    padding-top: max(1rem, env(safe-area-inset-top));
  }
`;

const ModalCard = styled.div<{ disableAnimation?: boolean }>`
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  border-radius: 24px;
  border: 1px solid #e5e7eb;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.02) inset;
  padding: 2rem;
  position: relative;
  margin: auto;

  .dark & {
    background: linear-gradient(180deg, #0f1216 0%, #0b0d13 100%);
    border-color: #2e3441;
    box-shadow: 0 24px 56px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  @media (min-width: 1024px) {
    padding: 2.25rem;
    max-width: 500px;
  }

  @media (max-width: 768px) {
    border-radius: 18px;
    max-width: 100%;
    padding: clamp(1.5rem, 3vw, 2rem);
  }

  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 1.5rem 1.25rem;
    margin-top: auto;
    margin-bottom: auto;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: clamp(1.2rem, 2.2vw, 1.4rem);
  font-weight: 800;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0b1220;
  line-height: 1.22;
  letter-spacing: -0.018em;
  text-rendering: optimizeLegibility;

  .dark & {
    color: #f1f5f9;
  }

  @media (min-width: 1024px) {
    font-size: 1.4rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.6rem;
  }
`;

const ModalSubtitle = styled.p`
  margin: 0 0 1.65rem 0;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  font-family: 'Inter', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #4c5566;
  line-height: 1.65;
  font-weight: 500;
  letter-spacing: 0.004em;

  .dark & {
    color: #c8d4f5;
  }

  @media (min-width: 1024px) {
    font-size: 1.05rem;
    margin-bottom: 1.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.4rem;
    font-size: 0.95rem;
  }
`;

const QuickChips = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.4rem;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.65rem;
    margin-bottom: 1.15rem;
  }

  @media (min-width: 769px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.875rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1.75rem;
  }
`;

const Chip = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>`
  background: ${p => p.isSelected 
    ? 'linear-gradient(180deg, #f7f9ff 0%, #f4f7ff 100%)' 
    : '#ffffff'};
  border: ${p => p.isSelected ? '1px solid #4b5563' : '1px solid #e5e7eb'};
  color: ${p => p.isSelected ? '#111827' : '#1f2937'};
  padding: clamp(0.625rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem);
  border-radius: 14px;
  font-size: clamp(0.9rem, 1.5vw, 0.98rem);
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${p => p.isSelected 
    ? '0 6px 18px rgba(0, 0, 0, 0.08)' 
    : '0 1px 2px rgba(0, 0, 0, 0.04)'};
  transform: translateY(0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.isSelected 
      ? '0 8px 20px rgba(0, 0, 0, 0.1)' 
      : '0 4px 10px rgba(0, 0, 0, 0.06)'};
    border-color: ${p => p.isSelected ? '#4b5563' : '#e6eaf0'};
    background: ${p => p.isSelected 
      ? 'linear-gradient(180deg, #f7f9ff 0%, #f4f7ff 100%)' 
      : '#fafbff'};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #4b5563;
    outline-offset: 2px;
  }

  .dark & {
    background: ${p => p.isSelected ? '#13161b' : '#0f1216'};
    border-color: ${p => p.isSelected ? '#494f5b' : '#2e3441'};
    color: ${p => p.isSelected ? '#ffffff' : '#e5e7eb'};
    box-shadow: ${p => p.isSelected 
      ? '0 12px 30px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.04)' 
      : '0 1px 2px rgba(0, 0, 0, 0.2)'};

    &:hover {
      border-color: ${p => p.isSelected ? '#494f5b' : '#3a4250'};
      box-shadow: ${p => p.isSelected 
        ? '0 16px 36px rgba(0, 0, 0, 0.6)' 
        : '0 4px 10px rgba(0, 0, 0, 0.3)'};
      background: ${p => p.isSelected ? '#13161b' : '#111317'};
    }
  }

  @media (min-width: 1024px) {
    min-height: 48px;
    padding: 0.75rem 1rem;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    min-height: 42px;
    padding: 0.625rem 0.75rem;
    font-size: 0.9rem;
    border-radius: 12px;
  }
`;

const ModalInput = styled.input`
  width: 100%;
  padding: clamp(0.875rem, 2vw, 1rem) clamp(1rem, 2.5vw, 1.25rem);
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  font-size: clamp(1.05rem, 2.2vw, 1.15rem);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  color: #111827;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), inset 0 0 0 1px rgba(0, 0, 0, 0.02);
  min-height: 52px;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.05em;
  will-change: border-color, box-shadow;

  &::placeholder {
    color: #b1b7c3;
    font-weight: 500;
    letter-spacing: 0;
  }

  &:focus {
    border-color: #4b5563;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(0, 0, 0, 0.02);
    background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  }

  .dark & {
    background: linear-gradient(180deg, #0f1216 0%, #0b0d13 100%);
    border-color: #2e3441;
    color: #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);

    &::placeholder {
      color: #64748b;
    }

    &:focus {
      border-color: #494f5b;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.04);
      background: linear-gradient(180deg, #0f1216 0%, #0b0d13 100%);
    }
  }

  @media (min-width: 1024px) {
    padding: 1rem 1.375rem;
    min-height: 54px;
    font-size: 1.15rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    min-height: 48px;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    border-radius: 12px;
  }
`;

const ModalActions = styled.div`
  margin-top: 1.75rem;
  display: flex;
  justify-content: center;
  gap: 0.875rem;
  flex-wrap: wrap;

  @media (min-width: 1024px) {
    margin-top: 2rem;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    margin-top: 1.25rem;
    flex-direction: column-reverse;
    gap: 0.625rem;
  }
`;

const ModalButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'primary' | 'ghost' }>`
  border: ${p => p.variant === 'primary' ? 'none' : '1px solid #e5e7eb'};
  background: ${p => p.variant === 'primary' 
    ? 'linear-gradient(180deg, #1f2937 0%, #111827 100%)' 
    : 'linear-gradient(180deg, #ffffff 0%, #fafbff 100%)'};
  color: ${p => p.variant === 'primary' ? '#ffffff' : '#111827'};
  padding: clamp(0.85rem, 2.1vw, 0.95rem) clamp(1.35rem, 3.2vw, 1.6rem);
  border-radius: 16px;
  font-size: clamp(0.95rem, 1.6vw, 1.05rem);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${p => p.variant === 'primary' 
    ? '0 4px 12px rgba(0, 0, 0, 0.12)' 
    : '0 1px 2px rgba(0, 0, 0, 0.04)'};
  transform: translateY(0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.variant === 'primary' 
      ? '0 6px 18px rgba(0, 0, 0, 0.16)' 
      : '0 4px 10px rgba(0, 0, 0, 0.06)'};
    background: ${p => p.variant === 'primary' 
      ? 'linear-gradient(180deg, #374151 0%, #1f2937 100%)' 
      : 'linear-gradient(180deg, #fafbff 0%, #f7f9ff 100%)'};
    border-color: ${p => p.variant === 'primary' ? 'none' : '#e6eaf0'};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #4b5563;
    outline-offset: 2px;
  }

  .dark & {
    background: ${p => p.variant === 'primary' 
      ? 'linear-gradient(180deg, #374151 0%, #1f2937 100%)' 
      : 'linear-gradient(180deg, #0f1216 0%, #0b0d13 100%)'};
    border-color: ${p => p.variant === 'primary' ? 'none' : '#2e3441'};
    color: ${p => p.variant === 'primary' ? '#ffffff' : '#e5e7eb'};
    box-shadow: ${p => p.variant === 'primary' 
      ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
      : '0 1px 2px rgba(0, 0, 0, 0.2)'};

    &:hover {
      background: ${p => p.variant === 'primary' 
        ? 'linear-gradient(180deg, #4b5563 0%, #374151 100%)' 
        : 'linear-gradient(180deg, #111317 0%, #0f1216 100%)'};
      box-shadow: ${p => p.variant === 'primary' 
        ? '0 6px 18px rgba(0, 0, 0, 0.4)' 
        : '0 4px 10px rgba(0, 0, 0, 0.3)'};
      border-color: ${p => p.variant === 'primary' ? 'none' : '#3a4250'};
    }
  }

  @media (min-width: 1024px) {
    padding: 0.95rem 1.625rem;
    min-height: 46px;
    font-size: 1.05rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-height: 48px;
    padding: 0.875rem 1.25rem;
    font-size: 0.9375rem;
    border-radius: 12px;
  }
`;

// Component Props
export interface BatteryHealthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void; // Parent handles the value extraction from ref
  onSkip: () => void;
  batteryInputRef: React.RefObject<HTMLInputElement>;
  batteryInputValueRef: React.MutableRefObject<string>;
  hasBatteryModalInteracted: boolean;
  onInteractionChange: (interacted: boolean) => void;
  t: (key: string) => string;
}

// Main Component
const BatteryHealthModal: React.FC<BatteryHealthModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onSkip,
  batteryInputRef,
  batteryInputValueRef,
  hasBatteryModalInteracted,
  onInteractionChange,
  t,
}) => {
  const handleBatteryInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Quick validation: extract digits (max 3) and check for %
    let digits = '';
    let hasPercent = false;
    let digitCount = 0;
    
    for (let i = 0; i < inputValue.length && digitCount < 3; i++) {
      const char = inputValue[i];
      if (char >= '0' && char <= '9') {
        digits += char;
        digitCount++;
      } else if (char === '%') {
        hasPercent = true;
      }
    }
    
    // Limit to maximum 100
    if (digits.length > 0) {
      const numValue = parseInt(digits);
      if (numValue > 100) {
        digits = '100';
      }
    }
    
    // Build final value
    const finalValue = digits + (hasPercent && digits.length > 0 ? '%' : '');

    // Update the input DOM directly (uncontrolled) - no re-render
    if (finalValue !== inputValue && batteryInputRef.current) {
      batteryInputRef.current.value = finalValue;
    }

    // Update ref for save function
    batteryInputValueRef.current = finalValue;
    
    // Set interaction flag once
    if (!hasBatteryModalInteracted) {
      onInteractionChange(true);
    }
  }, [hasBatteryModalInteracted, batteryInputValueRef, onInteractionChange]);

  const handleBatteryInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const currentValue = input.value;
    
    // Allow navigation and control keys
    if (e.key === 'Backspace' || e.key === 'Delete' || 
        e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
        e.key === 'Tab' || e.key === 'Enter' ||
        e.key === 'Home' || e.key === 'End' ||
        (e.ctrlKey || e.metaKey)) {
      return;
    }
    
    // Allow % sign, but only if there are digits and no % already
    if (e.key === '%') {
      let digitCount = 0;
      let hasPercent = false;
      for (let i = 0; i < currentValue.length; i++) {
        if (currentValue[i] >= '0' && currentValue[i] <= '9') {
          digitCount++;
        } else if (currentValue[i] === '%') {
          hasPercent = true;
        }
      }
      if (digitCount === 0 || hasPercent) {
        e.preventDefault();
      }
      return;
    }
    
    // Only allow digits
    if (e.key < '0' || e.key > '9') {
      e.preventDefault();
      return;
    }
    
    // Count existing digits
    let digitCount = 0;
    for (let i = 0; i < currentValue.length; i++) {
      if (currentValue[i] >= '0' && currentValue[i] <= '9') {
        digitCount++;
      }
    }
    
    // Prevent typing if already 3 digits
    if (digitCount >= 3) {
      e.preventDefault();
      return;
    }
    
    // Prevent typing if result would be > 100
    let digitsOnly = '';
    for (let i = 0; i < currentValue.length; i++) {
      if (currentValue[i] >= '0' && currentValue[i] <= '9') {
        digitsOnly += currentValue[i];
      }
    }
    const newValue = digitsOnly + e.key;
    const numValue = parseInt(newValue);
    if (!isNaN(numValue) && numValue > 100) {
      e.preventDefault();
      return;
    }
  }, []);

  if (!isOpen || typeof window === 'undefined') {
    return null;
  }

  return createPortal(
    <ModalOverlay
      disableAnimation={hasBatteryModalInteracted}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onSkip();
        }
      }}
    >
      <ModalCard
        disableAnimation={hasBatteryModalInteracted}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalTitle>{t('enterBatteryHealth') || 'Weet je het exacte batterijpercentage?'}</ModalTitle>
        <ModalSubtitle>{t('enterBatteryHealthSubtitle') || 'Vul de maximumcapaciteit (%) in zoals getoond in Instellingen ▸ Batterij ▸ Batterijconditie.'}</ModalSubtitle>
        <QuickChips>
          {["85%","88%","90%","92%","95%","98%"].map(v => {
            const numericValue = v.replace(/[^0-9]/g, '');
            const isSelected = batteryInputValueRef.current === numericValue;
            return (
              <Chip 
                key={v} 
                isSelected={isSelected}
                onClick={() => {
                  // Update DOM directly (uncontrolled) - no re-render
                  if (batteryInputRef.current) {
                    batteryInputRef.current.value = numericValue;
                  }
                  batteryInputValueRef.current = numericValue;
                  if (!hasBatteryModalInteracted) {
                    onInteractionChange(true);
                  }
                }}
              >
                {v}
              </Chip>
            );
          })}
        </QuickChips>
        <ModalInput
          ref={batteryInputRef}
          type="text"
          placeholder={`${t('forExample')} 88`}
          defaultValue=""
          onChange={handleBatteryInputChange}
          onKeyDown={handleBatteryInputKeyDown}
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
        />
        <ModalActions>
          <ModalButton variant="ghost" onClick={onSkip}>{t('skip') || 'Overslaan'}</ModalButton>
          <ModalButton variant="primary" onClick={onSave}>{t('save') || 'Opslaan'}</ModalButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>,
    getPortalRoot()
  );
};

export default BatteryHealthModal;

